import { useState, useEffect } from 'react';
import { decryptInvoiceData } from './serverjs/firsQRgenerator';

function InvoiceGenerator() {
  const [invoiceData, setInvoiceData] = useState('');
  const [showValidateBtn, setShowValidateBtn] = useState(false);
  const [showSubmitBtn, setShowSubmitBtn] = useState(false);
  const [showQRCodeBtn, setShowQRCodeBtn] = useState(false);
  const [errors, setErrors] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const [qrCodeImage, setQrCodeImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [qrcodeData, setQrcodeData] = useState(null);
  
  // Store data in state instead of window object
  const [finalData, setFinalData] = useState(null);
  const [formattedData, setFormattedData] = useState(null);
  const [ublData, setUblData] = useState(null);
  const [errorsFound, setErrorsFound] = useState([]);

  // Developer Mode States
  const [isDevelopment, setIsDevelopment] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [devPassword, setDevPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Payment Status Modal States
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('PAID');
  const [savingPaymentStatus, setSavingPaymentStatus] = useState(false);

  let invoiceKey = '';
  let creditNoteKey = '';

  // Helper function for postMessage communication
  async function postMessageWithResponse(message) {
    return new Promise((resolve) => {
      const requestId = crypto.randomUUID();

      function onMessage(event) {
        if (event.data?.type?.endsWith('-response') && event.data?.requestId === requestId) {
          window.removeEventListener('message', onMessage);
          resolve(event.data.body);
        }
      }

      window.addEventListener('message', onMessage);
      window.parent.postMessage({ ...message, requestId }, '*');
    });
  }

  // Initialize on component mount
  useEffect(() => {
    const initializePage = async () => {
      try {
        const formResponse = await postMessageWithResponse({
          type: 'page-request'
        });

        if (formResponse?.handler === "SalesInvoiceView") {
          invoiceKey = formResponse?.query.key;
        } else if (formResponse?.handler === "CreditNoteView") {
          creditNoteKey = formResponse?.query.key;
        } else {
          alert("This extension only works on Sales Invoice or Credit Note Page pages.");
        }

        //console.log('Generated Keys: Invoice: ' + invoiceKey + " Credit Note: " + creditNoteKey);
      } catch (error) {
        console.error('Error initializing page:', error);
      }
    };

    initializePage();
  }, []);

  // Import functions (these should be imported from your actual modules)
  // import { fnGetSalesInvoiceData, fnFetchLineItem, fetchTaxData, fnGetCreditNoteData } from './serverjs/mgrSalesModule';
  // import { getCompanyDetails } from './serverjs/companyDataFunction';
  // import { convertInvoiceData } from './serverjs/expoGenFunction';
  // import { ublConverterFunction } from './serverjs/ublConverter';

  const mencodeBase64 = (str) => {
    const utf8Bytes = new TextEncoder().encode(str);
    let binaryString = '';
    for (let i = 0; i < utf8Bytes.length; i++) {
      binaryString += String.fromCharCode(utf8Bytes[i]);
    }
    return btoa(binaryString);
  };

  const managerApi = async (method, path, body) => {
    return await postMessageWithResponse({
      type: 'api-request',
      method: method,
      path: path,
      body: body
    });
  };

  // Developer Mode Functions
  const handleDevModeToggle = () => {
    if (isDevelopment) {
      // If already in dev mode, turn it off
      setIsDevelopment(false);
    } else {
      // Show password modal
      setShowPasswordModal(true);
      setDevPassword('');
      setPasswordError('');
    }
  };

  const handlePasswordSubmit = () => {
    const correctPassword = process.env.REACT_APP_DEV_PASSWORD;
    
    if (devPassword === correctPassword) {
      setIsDevelopment(true);
      setShowPasswordModal(false);
      setDevPassword('');
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password. Please try again.');
    }
  };

  const handlePasswordCancel = () => {
    setShowPasswordModal(false);
    setDevPassword('');
    setPasswordError('');
  };

  const handlePasswordKeyPress = (e) => {
    if (e.key === 'Enter') {
      handlePasswordSubmit();
    }
  };

  const handleDecryptInvoiceData = async () => {
    try {
      setLoading(true);
      
      // Parse the current invoice data from textarea
      const currentData = JSON.parse(invoiceData);
      
      // Extract decryption parameters from the data
      const decryptParams = {
        iv_hex: currentData.iv_hex || currentData.data?.iv_hex || '',
        pub: currentData.pub || currentData.data?.pub || '',
        ciphertext: currentData.ciphertext || currentData.data?.ciphertext || '',
        api_key: currentData.api_key || currentData.data?.api_key || ''
      };

      // Validate that required parameters are present
      if (!decryptParams.iv_hex || !decryptParams.pub || !decryptParams.ciphertext || !decryptParams.api_key) {
        throw new Error('Missing required decryption parameters: iv_hex, pub, ciphertext, and api_key. Please ensure these fields are present in the invoice data.');
      }

      // Call the decryptInvoiceData function
      const result = await decryptInvoiceData(decryptParams);

      // Display the result in the textarea
      setInvoiceData(JSON.stringify(result, null, 2));
    } catch (error) {
      console.error('Error decrypting invoice data:', error);
      setInvoiceData(JSON.stringify({
        success: false,
        error: error.message
      }, null, 2));
    } finally {
      setLoading(false);
    }
  };

  const handleLoadData = async () => {
    setLoading(true);
    try {
      // Dynamic imports for your modules
      const { fnGetSalesInvoiceData, fnFetchLineItem, fetchTaxData, fnGetCreditNoteData } = 
        await import('./serverjs/mgrSalesModule');

      // Load credit note data if exists
      const crdData = creditNoteKey && await fnGetCreditNoteData(creditNoteKey);
      const formattedCreditData = crdData?.creditNoteData;

      // Determine invoice key
      const currentInvoiceKey = creditNoteKey ? formattedCreditData.SalesInvoice : invoiceKey;
      
      // Load invoice data
      const invData = currentInvoiceKey && await fnGetSalesInvoiceData(currentInvoiceKey);
      const formattedInvData = invData?.salesinvoicedata;

      // Collect validation errors
      const validationErrors = [];
      validationErrors.push(invData?.validationerrors || []);
      validationErrors.push(crdData?.validationerrors || []);
      setErrorsFound(validationErrors);

      // Validate the data
      const result = validateDataFunc(validationErrors);

      if (result) {
        setInvoiceData(formattedCreditData ? 
          JSON.stringify(formattedCreditData, null, 2) : 
          JSON.stringify(formattedInvData, null, 2)
        );

        const customerData = crdData ? crdData.customerData : invData.customerData;

        // Guard: ensure formattedInvData or formattedCreditData exists before accessing Lines
        if (!formattedInvData && !formattedCreditData) {
          displayErrors(['Failed to load invoice data - no data returned']);
          return;
        }

        // Extract item keys from Lines array
        const itemKeys = formattedCreditData ? 
          formattedCreditData.Lines.map(line => line.Item) : 
          formattedInvData.Lines.map(line => line.Item);

        // Fetch line items
        const lineItemsResult = await fnFetchLineItem(itemKeys);

        // Fetch tax data
        const taxCodes = await fetchTaxData();

        const loadedFinalData = {
          salesData: formattedInvData,
          customerData: customerData,
          inventory: lineItemsResult.inventoryData,
          nonInventory: lineItemsResult.nonInventoryData,
          taxData: taxCodes,
          creditData: formattedCreditData
        };

        setFinalData(loadedFinalData);
        setInvoiceData(JSON.stringify(loadedFinalData, null, 2));
        setShowValidateBtn(true);
      } else {
        setShowValidateBtn(false);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      displayErrors([error.message || 'Failed to load invoice data']);
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    setLoading(true);
    try {
      const { getCompanyDetails } = await import('./serverjs/companyDataFunction');
      const { convertInvoiceData } = await import('./serverjs/expoGenFunction');

      const companyApiInfo = await getCompanyDetails();

      // Check for missing fields
      const validationErrors = [];
      if (companyApiInfo?.missingFields && Array.isArray(companyApiInfo.missingFields)) {
        companyApiInfo.missingFields.forEach(field => {
          validationErrors.push(field + ' is missing');
        });
        setErrorsFound(validationErrors);
        
        if (validationErrors.length !== 0) {
          displayErrors(validationErrors);
          return;
        }
      } else {
        const recData = JSON.stringify(companyApiInfo, null, 2);
        setInvoiceData(recData);
        console.log('Company data retrieved successfully');
      }

      // Run validation
      const result = validateDataFunc([...errorsFound, ...validationErrors]);

      if (result) {
        // Guard: ensure required data is available before conversion
        if (!finalData || !companyApiInfo?.matchedFields) {
          displayErrors(['Missing required data: invoice data or company fields are undefined']);
          return;
        }

        // Deep validation of finalData properties
        const missingDataErrors = [];
        if (!finalData.salesData) missingDataErrors.push('Sales invoice data is missing');
        if (!finalData.customerData) missingDataErrors.push('Customer data is missing');
        if (!finalData.inventory && !finalData.nonInventory) missingDataErrors.push('Inventory/item data is missing');
        if (!finalData.taxData) missingDataErrors.push('Tax data is missing');

        if (missingDataErrors.length > 0) {
          displayErrors(missingDataErrors);
          return;
        }

        // Format data for submission
        const formatted = await convertInvoiceData(finalData, companyApiInfo?.matchedFields,companyApiInfo?.bizDetails);
        //console.log('Formatted Data:', JSON.stringify(formatted, null, 2));
        setFormattedData(formatted);
        setInvoiceData(JSON.stringify(formatted, null, 2));        

        /* NOW WE GOING TO FORMAT WIT JSON STYLE */

        // Import the jsonfyInvoiceData function
      const { jsonfyInvoiceData } = await import('./serverjs/expoGenFunction');

      // Validate that formattedData exists
      if (!formatted) {
        displayErrors(['No formatted invoice data available. Please validate first.']);
        return;
      }

      // Call jsonfyInvoiceData with formattedData
      const jsonifiedData = jsonfyInvoiceData(formatted);

      // Check if the conversion was successful
      if (!jsonifiedData) {
        displayErrors(['Failed to convert invoice data to JSON format.']);
        return;
      }

      // VALIDATION OF JSONIFIED DATA
      const jsonValidationErrors = [];

      // Valid tax categories
      const validTaxCategories = [
        "STANDARD_GST", "REDUCED_GST", "ZERO_GST", "STANDARD_VAT", "REDUCED_VAT",
        "ZERO_VAT", "STATE_SALES_TAX", "LOCAL_SALES_TAX", "ALCOHOL_EXCISE_TAX",
        "TOBACCO_EXCISE_TAX", "FUEL_EXCISE_TAX", "CORPORATE_INCOME_TAX",
        "PERSONAL_INCOME_TAX", "SOCIAL_SECURITY_TAX", "MEDICARE_TAX",
        "REAL_ESTATE_TAX", "PERSONAL_PROPERTY_TAX", "CARBON_TAX", "PLASTIC_TAX",
        "IMPORT_DUTY", "EXPORT_DUTY", "LUXURY_TAX", "SERVICE_TAX", "TOURISM_TAX",
        "WITHHOLDING_TAX", "STAMP_DUTY", "EXEMPTED"
      ];

      // 1. Check business_id (CompanyData.businessID)
      if (!formatted.CompanyData?.businessID || !formatted.CompanyData.businessID.trim()) {
        jsonValidationErrors.push('Business ID Missing');
      }

      // 2. Check irn (CompanyData.irnCode)
      if (!formatted.CompanyData?.irnCode || !formatted.CompanyData.irnCode.trim()) {
        jsonValidationErrors.push('IRN Missing');
      }

      // 3. Check accounting_supplier_party.telephone (companyBizData.telephone)
      const supplierPhone = formatted.companyBizData?.telephone;
      if (!supplierPhone || !supplierPhone.trim() || !supplierPhone.startsWith('+')) {
        jsonValidationErrors.push("Supplier phone number must start with '+' and cannot be empty");
      }

      // 4. Check accounting_supplier_party.country (companyBizData.country)
      const supplierCountry = formatted.companyBizData?.country;
      if (!supplierCountry || supplierCountry.trim() !== 'NG') {
        jsonValidationErrors.push("Supplier country must be 'NG'");
      }

      // 5. Check accounting_customer_party.telephone (customerData.record_telephone)
      const customerPhone = formatted.customerData?.record_telephone;
      if (!customerPhone || !customerPhone.trim() || !customerPhone.startsWith('+')) {
        jsonValidationErrors.push("Customer phone number must start with '+' and cannot be empty");
      }

      // 6. Check accounting_customer_party.country (customerData.record_country)
      const customerCountry = formatted.customerData?.record_country;
      if (!customerCountry || customerCountry.trim() !== 'NG') {
        jsonValidationErrors.push("Customer country must be 'NG'");
      }

      // 7. Check tax_total.tax_subtotal.tax_category.id (LineItems[].TaxCatCode)
      if (formatted.LineItems && Array.isArray(formatted.LineItems)) {
        formatted.LineItems.forEach((lineItem, index) => {
          const taxCategoryId = lineItem.TaxCatCode;
          if (!taxCategoryId || !validTaxCategories.includes(taxCategoryId)) {
            jsonValidationErrors.push(`Invalid tax category '${taxCategoryId || 'undefined'}' for line item ${index + 1} (${lineItem.ItemName}). Must be one of the valid tax codes.`);
          }
        });
      }

      // Display validation errors if any
      if (jsonValidationErrors.length > 0) {
        displayErrors(jsonValidationErrors);
        setShowQRCodeBtn(false);
        return;
      }

      // Display the result in the textarea
      setInvoiceData(JSON.stringify(jsonifiedData, null, 2));

        setShowValidateBtn(true);
        setShowSubmitBtn(false);
        setShowQRCodeBtn(true);
      }
    } catch (error) {
      console.error('Error validating:', error);
      displayErrors([error.message || 'Failed to validate invoice']);
      setShowQRCodeBtn(false);
    } finally {
      setLoading(false);
    }
  };

  const fnGenInvoicePostObject = async (qrCode, signedDoc) => {
    try {
      const path = formattedData.CreditUID ? 
        `/api2/credit-note-form/${formattedData.CreditUID}` : 
        `/api2/sales-invoice-form/${formattedData.InvoiceUID}`;

      // Get invoice data
      const formResponse = await managerApi('GET', path, null);
      let jsonObject = formResponse.body;

      // CustomFields keys
      const nestedKey = "CustomFields";
      const Acc_QRkey = "9f2f24c4-409b-41c0-94da-195130ea4a86";
      const Acc_SignedDocKey = "65c011b6-98e6-4cf5-a8ac-d429ffd3bfed";

      // Ensure CustomFields exists
      if (!jsonObject[nestedKey] || typeof jsonObject[nestedKey] !== 'object') {
        jsonObject[nestedKey] = {};
      }

      // Add QR code and signed doc
      jsonObject[nestedKey][Acc_QRkey] = qrCode;
      jsonObject[nestedKey][Acc_SignedDocKey] = signedDoc;

      // Enable invoice footer
      if (formattedData.CreditUID) {
        jsonObject["HasCreditNoteFooters"] = "true";
        jsonObject["CreditNoteFooters"] = ["8a606e0f-945f-4e86-bd79-e4a423b56f4a"];
      } else {
        jsonObject["HasSalesInvoiceFooters"] = "true";
        jsonObject["SalesInvoiceFooters"] = ["e62b0842-1024-4bc3-9a7f-d51702faa3b5"];
      }

      // Post to Manager
      const response = await managerApi('POST', path, jsonObject);
      console.log('Manager Posting Result:', response);

      return JSON.stringify(jsonObject, (key, value) => value === null ? undefined : value);
    } catch (ex) {
      console.error(`Post to Manager Error: ${ex.message}`);
      return false;
    }
  };

  const handleSubmitInvoice = async () => {
    setLoading(true);
    try {
      
      // Import the jsonfyInvoiceData function
      const { jsonfyInvoiceData } = await import('./serverjs/expoGenFunction');

      // Validate that formattedData exists
      if (!formattedData) {
        displayErrors(['No formatted invoice data available. Please validate first.']);
        return;
      }

      // Call jsonfyInvoiceData with formattedData
      const jsonifiedData = jsonfyInvoiceData(formattedData);

      // Check if the conversion was successful
      if (!jsonifiedData) {
        displayErrors(['Failed to convert invoice data to JSON format.']);
        return;
      }

      // Display the result in the textarea
      setInvoiceData(JSON.stringify(jsonifiedData, null, 2));

      // Validate company data exists
      if (!formattedData.CompanyData || !formattedData.CompanyData.baseURL) {
        displayErrors(['Company base URL is missing.']);
        return;
      }

      // POST jsonifiedData to validation endpoint
      const validationPayload = {
        url: formattedData.CompanyData.baseURL,
        apiKey : formattedData.CompanyData.clientID,
        apiSecret : formattedData.CompanyData.clientSecretKey,
        invoiceData: jsonifiedData
      };

      console.log('📤 Posting to validation endpoint:', validationPayload);

      // Empty string fallback ensures relative URLs (/api/...) are used in production
      const API_BASE_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/invoice/sign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(validationPayload)
      });

      const validationResult = await response.json();

      console.log('✅ Validation Response:', validationResult);

      if (!response.ok) {
        displayErrors([`Validation failed: ${validationResult.error?.error?.details || 'Unknown error'}`]);
        setInvoiceData(JSON.stringify(validationResult, null, 2));
        return;
      }

      // Patch the invoice with QR code and IRN code
      const { patchSalesInvoiceImage } = await import('./serverjs/funcInvoicePatch');
      const patchResult = await patchSalesInvoiceImage(
        formattedData.InvoiceUID,
        formattedData.CreditUID,
        formattedData.irnCode,
        qrCodeImage
      );
      console.log('Invoice patching result:', patchResult);

      //Success - display validation result
      setInvoiceData(JSON.stringify(validationResult.data, null, 2));

      setShowSubmitBtn(false);

      await postMessageWithResponse({
                        type: 'reload'
                    });

    } catch (error) {
      console.error('❌ Error submitting invoice:', error);
      displayErrors([error.message || 'Failed to submit invoice']);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitInvoice_Backup = async () => {
    setLoading(true);
    try {
      const jsonDataFor = {
        invoice: mencodeBase64(ublData)
      };

      const textEncoder = new TextEncoder();
      const jsonDataBytes = textEncoder.encode(JSON.stringify(jsonDataFor, null, 2));

      const response = await fetch('/Home/JofotaraPost', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ClientID: formattedData.CompanyData.clientID,
          SecretKey: formattedData.CompanyData.clientSecretKey,
          jsonData: Array.from(jsonDataBytes)
        })
      });

      const result = await response.text();
      const resultJ = JSON.parse(result);

      console.log(resultJ.EINV_QR);
      setInvoiceData(JSON.stringify(resultJ, null, 2));

      // Display QR code if available
      if (resultJ.EINV_QR) {
        const qrCodeGen = `<img height='100' width='100' src='https://api.qrserver.com/v1/create-qr-code/?data=${resultJ.EINV_QR}' alt='' title='' />`;
        await fnGenInvoicePostObject(qrCodeGen, resultJ.EINV_SINGED_INVOICE);

        // Download and display QR code
        const qrData = resultJ.EINV_QR;
        const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(qrData)}`;

        try {
          const qrResponse = await fetch(qrUrl);
          const blob = await qrResponse.blob();
          const reader = new FileReader();
          
          reader.onload = function(e) {
            setQrCodeImage(e.target.result);
          };
          
          reader.readAsDataURL(blob);
        } catch (error) {
          console.error('Error downloading QR code:', error);
          // Fallback to base64
          setQrCodeImage('data:image/png;base64,' + resultJ.EINV_QR);
        }
      }
    } catch (error) {
      console.error('Error submitting invoice:', error);
      displayErrors([error.message || 'Failed to submit invoice']);
    } finally {
      setLoading(false);
    }
  };


  const handleSubmitInvoice_V1 = async () => {
    setLoading(true);
    try {
      // Import the jsonfyInvoiceData function
      const { jsonfyInvoiceData } = await import('./serverjs/expoGenFunction');

      // Validate that formattedData exists
      if (!formattedData) {
        displayErrors(['No formatted invoice data available. Please validate first.']);
        return;
      }

      // Call jsonfyInvoiceData with formattedData
      const jsonifiedData = jsonfyInvoiceData(formattedData);

      // Check if the conversion was successful
      if (!jsonifiedData) {
        displayErrors(['Failed to convert invoice data to JSON format.']);
        return;
      }

      // Display the result in the textarea
      setInvoiceData(JSON.stringify(jsonifiedData, null, 2));

      // Validate company data exists
      if (!formattedData.CompanyData || !formattedData.CompanyData.baseURL) {
        displayErrors(['Company base URL is missing.']);
        return;
      }

      // POST jsonifiedData to validation endpoint
      const validationPayload = {
        url: formattedData.CompanyData.baseURL,
        apiKey : formattedData.CompanyData.clientID,
        apiSecret : formattedData.CompanyData.clientSecretKey,
        invoiceData: jsonifiedData
      };

      console.log('📤 Posting to validation endpoint:', validationPayload);

      // Empty string fallback ensures relative URLs (/api/...) are used in production
      const API_BASE_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/invoice/sign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(validationPayload)
      });

      const validationResult = await response.json();

      console.log('✅ Validation Response:', validationResult);

      if (!response.ok) {
        displayErrors([`Validation failed: ${validationResult.error?.error?.details || 'Unknown error'}`]);
        setInvoiceData(JSON.stringify(validationResult, null, 2));
        return;
      }

      //Success - display validation result
      setInvoiceData(JSON.stringify(validationResult.data, null, 2));
      setShowSubmitBtn(false);

      

    } catch (error) {
      console.error('❌ Error submitting invoice:', error);
      displayErrors([error.message || 'Failed to submit invoice']);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQRCode = async () => {
    setLoading(true);
    try {
      // Validate formatted data exists
      if (!formattedData) {
        displayErrors(['No invoice data available. Please validate first.']);
        return;
      }

      // Validation Rule 1: Check compulsory fields
      const validationErrors = [];

      // Check irnCode
      if (!formattedData.irnCode || !formattedData.irnCode.trim()) {
        validationErrors.push('IRN Code is missing');
      }

      // Check CompanyData exists and has required properties
      if (!formattedData.CompanyData) {
        validationErrors.push('Company Data object is missing');
      } else {
        const requiredCompanyFields = {
          'baseURL': formattedData.CompanyData.baseURL,
          'clientID': formattedData.CompanyData.clientID,
          'clientSecretKey': formattedData.CompanyData.clientSecretKey,
          'certificate': formattedData.CompanyData.certificate,
          'publickey': formattedData.CompanyData.publickey
        };

        Object.entries(requiredCompanyFields).forEach(([fieldName, fieldValue]) => {
          if (!fieldValue || (typeof fieldValue === 'string' && !fieldValue.trim())) {
            validationErrors.push(`${fieldName} is missing`);
          }
        });
      }

      // Display errors if validation fails
      if (validationErrors.length > 0) {
        displayErrors(validationErrors);
        setShowSubmitBtn(false);
        return;
      }

      // NEW: Validate invoice before generating QR code
      const { jsonfyInvoiceData } = await import('./serverjs/expoGenFunction');
      const jsonifiedData = jsonfyInvoiceData(formattedData);

      if (!jsonifiedData) {
        displayErrors(['Failed to convert invoice data to JSON format.']);
        setShowSubmitBtn(false);
        return;
      }

      const validationPayload = {
        url: formattedData.CompanyData.baseURL,
        apiKey: formattedData.CompanyData.clientID,
        apiSecret: formattedData.CompanyData.clientSecretKey,
        invoiceData: jsonifiedData
      };

      //console.log('📤 Validating invoice before QR generation:', validationPayload);

      // Empty string fallback ensures relative URLs (/api/...) are used in production
      const API_BASE_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/invoice/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(validationPayload)
      });

      let validationResult;
      const validateContentType = response.headers.get('content-type');
      if (validateContentType && validateContentType.includes('application/json')) {
        validationResult = await response.json();
      } else {
        const rawText = await response.text();
        console.error('Non-JSON response from /api/invoice/validate:', rawText);
        displayErrors([`Invoice validation failed: Server returned non-JSON response. Check server logs.`]);
        setShowSubmitBtn(false);
        return;
      }

      if (!response.ok) {
        displayErrors([`Invoice validation failed: ${validationResult.error?.error?.details || 'Unknown error'}`]);
        setInvoiceData(JSON.stringify(validationResult, null, 2));
        setShowSubmitBtn(false);
        return;
      }

      //console.log('✅ Invoice validation passed, proceeding with QR generation');

      // Validation Rule 2: Call validateIRN function via API
      const irnValidationPayload = {
        irnCode: formattedData.irnCode,
        firsBaseUrl: formattedData.CompanyData.baseURL,
        apiKey: formattedData.CompanyData.clientID,
        secretKey: formattedData.CompanyData.clientSecretKey

      };

      //console.log('📤 Validating IRN via API:', irnValidationPayload);

      const irnResponse = await fetch(`${API_BASE_URL}/api/irn/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(irnValidationPayload)
      });

      let irnValidationResult;
      const irnContentType = irnResponse.headers.get('content-type');
      if (irnContentType && irnContentType.includes('application/json')) {
        irnValidationResult = await irnResponse.json();
      } else {
        const rawText = await irnResponse.text();
        console.error('Non-JSON response from /api/irn/confirm:', rawText);
        displayErrors([`IRN validation failed: Server returned non-JSON response. Check server logs.`]);
        setShowSubmitBtn(false);
        return;
      }

      //console.log('✅ IRN Validation Response:', irnValidationResult);

      // Check if IRN is valid
      if (irnResponse.ok || irnValidationResult?.isValid) {
        displayErrors([irnValidationResult.message || 'IRN validation failed. Please check the IRN code already taken.']);

      
      setShowSubmitBtn(false);
        //return;
      } else {
        setShowSubmitBtn(true);
      }

       //setShowSubmitBtn(true);
      // IRN is valid, enable submit button
      //setShowErrors(false);
      //setErrors([]);

      // Call generateInvoiceQRCode with formattedData values
      const { generateInvoiceQRCode } = await import('./serverjs/firsQRgenerator');
      
      const qrParams = {
        irn: formattedData.irnCode,
        certificate: formattedData.CompanyData.certificate,
        publicKey: formattedData.CompanyData.publickey,
        size: 150,
        fgColor: "#000000",
        bgColor: "#FFFFFF"
      };

      //console.log('QR Params being sent:', JSON.stringify(qrParams, null, 2));

      try {
        const qrResult = await generateInvoiceQRCode(qrParams);
        
        if (qrResult.success) {
          // Display result in textarea
          setInvoiceData(JSON.stringify(qrResult.data, null, 2));
          //setShowSubmitBtn(true);
          // Store QR code data
          setQrcodeData(qrResult.data);
          // Show QR code in preview box
          if (qrResult.data?.qrCodeDataUrl) {
            setQrCodeImage(qrResult.data.qrCodeDataUrl);

            ////

            // // Patch the invoice with QR code and IRN code
            // const { patchSalesInvoiceImage } = await import('./serverjs/funcInvoicePatch');

            // console.log("params" ,formattedData.InvoiceUID,
            //   formattedData.CreditUID,
            //   formattedData.irnCode)

            // const patchResult = await patchSalesInvoiceImage(
            //   formattedData.InvoiceUID,
            //   formattedData.CreditUID,
            //   formattedData.irnCode,
            //   qrResult.data.qrCodeDataUrl
            // );
            // console.log('Invoice patching result:', patchResult);


            ////

          }
        } else {
          displayErrors([`QR Code generation failed: ${qrResult.error}`]);
          setShowSubmitBtn(false);
        }
      } catch (error) {
        console.error('Error calling generateInvoiceQRCode:', error);
        displayErrors([error.message || 'Failed to generate QR code']);
        setShowSubmitBtn(false);
      }
    } catch (error) {
      console.error('Error generating QR code:', error);
      displayErrors([error.message || 'Failed to generate QR code']);
      setShowSubmitBtn(false);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePaymentStatus = async () => {
    setShowPaymentModal(true);
  };

  const handleSavePaymentStatus = async () => {
    setSavingPaymentStatus(true);
    try {
      if (!formattedData) {
        displayErrors(['No invoice data available. Cannot update payment status.']);
        return;
      }

      const API_BASE_URL = process.env.REACT_APP_API_URL || '';

      const updatePaymentPayload = {
        irnCode: formattedData.irnCode,
        firsBaseUrl: formattedData.CompanyData.baseURL,
        apiKey: formattedData.CompanyData.clientID,
        secretKey: formattedData.CompanyData.clientSecretKey,
        payment_status: paymentStatus,   // PENDING | PAID | REJECTED (from dropdown)
        reference: formattedData.InvoiceID || formattedData.CreditID || ''
      };

      console.log('📤 Updating payment status:', updatePaymentPayload);

      const response = await fetch(`${API_BASE_URL}/api/irn/update`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatePaymentPayload)
      });

      let result;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        result = await response.json();
      } else {
        const rawText = await response.text();
        console.error('Non-JSON response from /api/irn/update:', rawText);
        displayErrors(['Payment status update failed: Server returned non-JSON response. Check server logs.']);
        return;
      }

      if (!response.ok) {
        displayErrors([`Payment status update failed: ${result?.message || result?.error || 'Unknown error'}`]);
        console.error('❌ Payment status update failed:', result);
        return;
      }

      console.log('✅ Payment status updated successfully:', result);
      setShowPaymentModal(false);

    } catch (error) {
      console.error('❌ Error updating payment status:', error);
      displayErrors([error.message || 'Failed to update payment status']);
    } finally {
      setSavingPaymentStatus(false);
    }
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
  };

    const handleGenerateQRCode_V1_Running_Backup = async () => {
    setLoading(true);
    try {
      // Validate formatted data exists
      if (!formattedData) {
        displayErrors(['No invoice data available. Please validate first.']);
        return;
      }

      // Validation Rule 1: Check compulsory fields
      const validationErrors = [];

      // Check irnCode
      if (!formattedData.irnCode || !formattedData.irnCode.trim()) {
        validationErrors.push('IRN Code is missing');
      }

      // Check CompanyData exists and has required properties
      if (!formattedData.CompanyData) {
        validationErrors.push('Company Data object is missing');
      } else {
        const requiredCompanyFields = {
          'baseURL': formattedData.CompanyData.baseURL,
          'clientID': formattedData.CompanyData.clientID,
          'clientSecretKey': formattedData.CompanyData.clientSecretKey,
          'certificate': formattedData.CompanyData.certificate,
          'publickey': formattedData.CompanyData.publickey
        };

        Object.entries(requiredCompanyFields).forEach(([fieldName, fieldValue]) => {
          if (!fieldValue || (typeof fieldValue === 'string' && !fieldValue.trim())) {
            validationErrors.push(`${fieldName} is missing`);
          }
        });
      }

      // Display errors if validation fails
      if (validationErrors.length > 0) {
        displayErrors(validationErrors);
        setShowSubmitBtn(false);
        return;
      }

      // NEW: Validate invoice before generating QR code
      const { jsonfyInvoiceData } = await import('./serverjs/expoGenFunction');
      const jsonifiedData = jsonfyInvoiceData(formattedData);

      if (!jsonifiedData) {
        displayErrors(['Failed to convert invoice data to JSON format.']);
        setShowSubmitBtn(false);
        return;
      }

      const validationPayload = {
        url: formattedData.CompanyData.baseURL,
        apiKey: formattedData.CompanyData.clientID,
        apiSecret: formattedData.CompanyData.clientSecretKey,
        invoiceData: jsonifiedData
      };

      //console.log('📤 Validating invoice before QR generation:', validationPayload);

      // Empty string fallback ensures relative URLs (/api/...) are used in production
      const API_BASE_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/invoice/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(validationPayload)
      });

      let validationResult;
      const validateContentType = response.headers.get('content-type');
      if (validateContentType && validateContentType.includes('application/json')) {
        validationResult = await response.json();
      } else {
        const rawText = await response.text();
        console.error('Non-JSON response from /api/invoice/validate:', rawText);
        displayErrors([`Invoice validation failed: Server returned non-JSON response. Check server logs.`]);
        setShowSubmitBtn(false);
        return;
      }

      if (!response.ok) {
        displayErrors([`Invoice validation failed: ${validationResult.error?.error?.details || 'Unknown error'}`]);
        setInvoiceData(JSON.stringify(validationResult, null, 2));
        setShowSubmitBtn(false);
        return;
      }

      //console.log('✅ Invoice validation passed, proceeding with QR generation');

      // Validation Rule 2: Call validateIRN function via API
      const irnValidationPayload = {
        irnCode: formattedData.irnCode,
        firsBaseUrl: formattedData.CompanyData.baseURL,
        apiKey: formattedData.CompanyData.clientID,
        secretKey: formattedData.CompanyData.clientSecretKey

      };

      //console.log('📤 Validating IRN via API:', irnValidationPayload);

      const irnResponse = await fetch(`${API_BASE_URL}/api/irn/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(irnValidationPayload)
      });

      let irnValidationResult;
      const irnContentType = irnResponse.headers.get('content-type');
      if (irnContentType && irnContentType.includes('application/json')) {
        irnValidationResult = await irnResponse.json();
      } else {
        const rawText = await irnResponse.text();
        console.error('Non-JSON response from /api/irn/confirm:', rawText);
        displayErrors([`IRN validation failed: Server returned non-JSON response. Check server logs.`]);
        setShowSubmitBtn(false);
        return;
      }

      //console.log('✅ IRN Validation Response:', irnValidationResult);

      // Check if IRN is valid
      if (irnResponse.ok || irnValidationResult?.isValid) {
        displayErrors([irnValidationResult.message || 'IRN validation failed. Please check the IRN code already taken.']);

      
      setShowSubmitBtn(false);
        //return;
      } else {
        setShowSubmitBtn(true);
      }

       //setShowSubmitBtn(true);
      // IRN is valid, enable submit button
      //setShowErrors(false);
      //setErrors([]);

      // Call generateInvoiceQRCode with formattedData values
      const { generateInvoiceQRCode } = await import('./serverjs/firsQRgenerator');
      
      const qrParams = {
        irn: formattedData.irnCode,
        certificate: formattedData.CompanyData.certificate,
        publicKey: formattedData.CompanyData.publickey,
        size: 300,
        fgColor: "#000000",
        bgColor: "#FFFFFF"
      };

      //console.log('QR Params being sent:', JSON.stringify(qrParams, null, 2));

      try {
        const qrResult = await generateInvoiceQRCode(qrParams);
        
        if (qrResult.success) {
          // Display result in textarea
          setInvoiceData(JSON.stringify(qrResult.data, null, 2));
          //setShowSubmitBtn(true);
          // Store QR code data
          setQrcodeData(qrResult.data);
          // Show QR code in preview box
          if (qrResult.data?.qrCodeDataUrl) {
            setQrCodeImage(qrResult.data.qrCodeDataUrl);

            ////

            // Patch the invoice with QR code and IRN code
            //const { patchSalesInvoice } = await import('./serverjs/funcInvoicePatch');

            // console.log("params" ,formattedData.InvoiceUID,
            //   formattedData.CreditUID,
            //   formattedData.irnCode)

            // const patchResult = await patchSalesInvoice(
            //   formattedData.InvoiceUID,
            //   formattedData.CreditUID,
            //   formattedData.irnCode,
            //   qrResult.data.qrCodeDataUrl
            // );
            // console.log('Invoice patching result:', patchResult);


            ////

          }
        } else {
          displayErrors([`QR Code generation failed: ${qrResult.error}`]);
          setShowSubmitBtn(false);
        }
      } catch (error) {
        console.error('Error calling generateInvoiceQRCode:', error);
        displayErrors([error.message || 'Failed to generate QR code']);
        setShowSubmitBtn(false);
      }
    } catch (error) {
      console.error('Error generating QR code:', error);
      displayErrors([error.message || 'Failed to generate QR code']);
      setShowSubmitBtn(false);
    } finally {
      setLoading(false);
    }
  };

  const validateDataFunc = (errorsList = errorsFound) => {
    // Guard: check if errorsList exists and has items
    if (!errorsList || errorsList.length === 0) {
      setShowErrors(false);
      return true;
    }

    // Flatten the errors array
    const flatErrors = [];
    errorsList.forEach(error => {
      if (Array.isArray(error) && error.length > 0) {
        flatErrors.push(...error);
      } else if (typeof error === 'string' && error.trim()) {
        flatErrors.push(error);
      }
    });

    // Check if there are any actual errors
    if (flatErrors.length > 0) {
      displayErrors(flatErrors);
      return false;
    }

    setShowErrors(false);
    return true;
  };

  const displayErrors = (errorsList) => {
    const flatErrors = [];
    errorsList.forEach(error => {
      if (Array.isArray(error)) {
        flatErrors.push(...error);
      } else if (error) {
        flatErrors.push(error);
      }
    });
    
    setErrors(flatErrors);
    setShowErrors(flatErrors.length > 0);
    setShowSubmitBtn(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold text-white text-center mb-8">Invoice Form Validation</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2">
            {/* Buttons Container with Developer Mode Toggle */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <button 
                  onClick={handleLoadData}
                  disabled={loading}
                  className="px-6 py-3 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-800 disabled:cursor-not-allowed text-white font-semibold rounded-lg shadow-lg transform hover:scale-105 transition-all duration-200 disabled:transform-none"
                >
                  {loading ? 'Loading...' : 'Load Data'}
                </button>
                
                {showValidateBtn && (
                  <button 
                    onClick={handleValidate}
                    disabled={loading}
                    className="px-6 py-3 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-800 disabled:cursor-not-allowed text-white font-semibold rounded-lg shadow-lg transform hover:scale-105 transition-all duration-200 disabled:transform-none"
                  >
                    {loading ? 'Validating...' : 'Validate'}
                  </button>
                )}
                
                {showQRCodeBtn && (
                  <button 
                    onClick={handleGenerateQRCode}
                    disabled={loading}
                    className="px-6 py-3 bg-purple-500 hover:bg-purple-600 disabled:bg-purple-800 disabled:cursor-not-allowed text-white font-semibold rounded-lg shadow-lg transform hover:scale-105 transition-all duration-200 disabled:transform-none"
                  >
                    {loading ? 'Generating...' : 'Generate QR Code'}
                  </button>
                )}

                {showSubmitBtn && (
                  <button 
                    onClick={handleSubmitInvoice}
                    disabled={loading}
                    className="px-6 py-3 bg-green-500 hover:bg-green-600 disabled:bg-green-800 disabled:cursor-not-allowed text-white font-semibold rounded-lg shadow-lg transform hover:scale-105 transition-all duration-200 disabled:transform-none"
                  >
                    {loading ? 'Submitting...' : 'Submit for Validation'}
                  </button>
                )}
              </div>

              {/* Developer Mode Toggle */}
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
                <span className="text-white text-sm font-medium">Developer Mode</span>
                <button
                  onClick={handleDevModeToggle}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${
                    isDevelopment ? 'bg-cyan-500' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isDevelopment ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                {isDevelopment && (
                  <span className="text-cyan-400 text-xs font-semibold">ON</span>
                )}
              </div>
            </div>

            {/* Error Display Panel */}
            {showErrors && errors.length > 0 && (
              <div className="bg-red-500/20 border border-red-500 rounded-lg p-4 mb-6">
                <h5 className="text-xl font-bold text-red-300 mb-3">Validation Errors</h5>
                <div className="space-y-2">
                  {errors.map((error, index) => (
                    <p key={index} className="text-red-200 flex items-start mb-1">
                      <span className="mr-2">•</span>
                      <span>{error}</span>
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Development Mode: Text Area */}
            {isDevelopment ? (
              <div className="bg-white/10 backdrop-blur-md rounded-xl shadow-2xl p-6">
                <label htmlFor="invoiceDataTextbox" className="block text-lg font-semibold text-white mb-3">
                  Invoice Data (Development Mode)
                </label>
                <textarea 
                  id="invoiceDataTextbox"
                  value={invoiceData}
                  onChange={(e) => setInvoiceData(e.target.value)}
                  className={`w-full h-96 p-4 bg-gray-800 text-white rounded-lg border ${
                    showErrors ? 'border-red-500 shadow-red-500/50' : 'border-gray-600'
                  } focus:border-cyan-500 focus:outline-none resize-none placeholder-gray-400 font-mono text-sm`}
                  placeholder="Paste invoice data here..."
                />
                <button
                  onClick={handleDecryptInvoiceData}
                  className="hidden mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200"
                >
                  Test Me
                </button>
              </div>
            ) : (
              /* Production Mode: Information Panel */
              <div className="bg-white/10 backdrop-blur-md rounded-xl shadow-2xl p-6">
                <h2 className="text-2xl font-bold text-white mb-6">Invoice Status</h2>
                
                {/* Status Cards */}
                <div className="space-y-4">
                  {/* Invoice Status */}
                  {formattedData && (
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-300 font-medium">Invoice Number:</span>
                        <span className="text-white font-semibold">
                          {formattedData.InvoiceID || formattedData.CreditID || 'N/A'}
                        </span>
                      </div>
                      {formattedData.irnCode && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-300 font-medium">IRN Code:</span>
                          <span className="text-cyan-400 font-mono text-sm">
                            {formattedData.irnCode}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Processing Status */}
                  <div className="bg-gray-800/50 rounded-lg p-4">
                    <h3 className="text-white font-semibold mb-3">Processing Status</h3>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full mr-3 ${finalData ? 'bg-green-500' : 'bg-gray-500'}`}></div>
                        <span className="text-gray-300">Data Loaded</span>
                      </div>
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full mr-3 ${formattedData ? 'bg-green-500' : 'bg-gray-500'}`}></div>
                        <span className="text-gray-300">Data Validated</span>
                      </div>
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full mr-3 ${qrCodeImage ? 'bg-green-500' : 'bg-gray-500'}`}></div>
                        <span className="text-gray-300">QR Code Generated</span>
                      </div>
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full mr-3 ${showSubmitBtn ? 'bg-yellow-500 animate-pulse' : 'bg-gray-500'}`}></div>
                        <span className="text-gray-300">Ready for Submission</span>
                      </div>
                    </div>
                  </div>

                  {/* Company Info */}
                  {formattedData?.CompanyData && (
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <h3 className="text-white font-semibold mb-3">Company Information</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-400">API Connected:</span>
                          <span className={`font-medium ${formattedData.CompanyData.baseURL ? 'text-green-400' : 'text-red-400'}`}>
                            {formattedData.CompanyData.baseURL ? 'Yes' : 'No'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - QR Code Preview */}
          <div className="lg:col-span-1">
            <div className="bg-white/10 backdrop-blur-md rounded-xl shadow-2xl p-6 text-center sticky top-8">
              <div className="mb-4">
                <small className="text-gray-300 text-sm">Generated QR Code Image</small>
              </div>
              
              {qrCodeImage ? (
                <div className="mt-4 flex justify-center">
                  <img 
                    src={qrCodeImage} 
                    alt="QR Code Preview" 
                    className="w-64 h-64 object-cover rounded-lg border-2 border-green-500 shadow-lg shadow-green-500/50"
                  />
                </div>
              ) : (
                <div className="mt-4 flex justify-center">
                  <div className="w-64 h-64 bg-gray-800 rounded-lg border-2 border-dashed border-gray-600 flex items-center justify-center">
                    <div className="text-center">
                      <svg className="w-16 h-16 mx-auto text-gray-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-gray-400 text-sm">No QR Code Generated</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Update Payment Status Button */}
              {qrCodeImage && !showSubmitBtn && !formattedData.CreditID &&  (
                <div className="mt-6">
                  <button
                    onClick={handleUpdatePaymentStatus}
                    disabled={loading}
                    className="w-full px-4 py-3 bg-yellow-500 hover:bg-yellow-600 disabled:bg-yellow-800 disabled:cursor-not-allowed text-white font-semibold rounded-lg shadow-lg transform hover:scale-105 transition-all duration-200 disabled:transform-none"
                  >
                    {loading ? 'Updating...' : 'Update Payment Status'}
                  </button>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
            <h2 className="text-2xl font-bold text-white mb-4">Enter Developer Password</h2>
            <p className="text-gray-300 mb-6 text-sm">Please enter the password to enable developer mode.</p>
            
            <input
              type="password"
              value={devPassword}
              onChange={(e) => setDevPassword(e.target.value)}
              onKeyPress={handlePasswordKeyPress}
              placeholder="Enter password"
              className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-cyan-500 focus:outline-none mb-4"
              autoFocus
            />
            
            {passwordError && (
              <p className="text-red-400 text-sm mb-4">{passwordError}</p>
            )}
            
            <div className="flex gap-3">
              <button
                onClick={handlePasswordSubmit}
                className="flex-1 px-4 py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-lg transition-colors duration-200"
              >
                Confirm
              </button>
              <button
                onClick={handlePasswordCancel}
                className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Status Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-md mx-4 border border-gray-700">

            {/* Modal Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Update Payment Status</h2>
              <button
                onClick={handleClosePaymentModal}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Invoice Details */}
            <div className="bg-gray-700/50 rounded-lg p-4 mb-6 space-y-3">
              <h3 className="text-gray-300 text-sm font-semibold uppercase tracking-wide mb-3">Invoice Details</h3>

              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-sm">Invoice No:</span>
                <span className="text-white font-semibold text-sm">
                  {formattedData?.InvoiceID || formattedData?.CreditID || 'N/A'}
                </span>
              </div>

              <div className="border-t border-gray-600"></div>

              <div className="flex items-start justify-between gap-4">
                <span className="text-gray-400 text-sm flex-shrink-0">IRN Code:</span>
                <span className="text-cyan-400 font-mono text-xs text-right break-all">
                  {formattedData?.irnCode || 'N/A'}
                </span>
              </div>
            </div>

            {/* Payment Status Dropdown */}
            <div className="mb-6">
              <label className="block text-gray-300 text-sm font-semibold mb-2">
                Payment Status
              </label>
              <div className="relative">
                <select
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-cyan-500 focus:outline-none appearance-none cursor-pointer"
                >
                  {/* <option value="PENDING">⏳ PENDING</option> */}
                  <option value="PAID">✅ PAID</option>
                  <option value="REJECTED">❌ REJECTED</option>
                </select>
                <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* Status Badge Preview */}
              <div className="mt-3 flex justify-center">
                <span className={`px-4 py-1 rounded-full text-sm font-semibold ${
                  paymentStatus === 'PAID'
                    ? 'bg-green-500/20 text-green-400 border border-green-500'
                    : paymentStatus === 'REJECTED'
                    ? 'bg-red-500/20 text-red-400 border border-red-500'
                    : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500'
                }`}>
                  {paymentStatus}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleSavePaymentStatus}
                disabled={savingPaymentStatus}
                className="flex-1 px-4 py-3 bg-green-500 hover:bg-green-600 disabled:bg-green-800 disabled:cursor-not-allowed text-white font-semibold rounded-lg shadow-lg transition-all duration-200"
              >
                {savingPaymentStatus ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleClosePaymentModal}
                disabled={savingPaymentStatus}
                className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-700 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors duration-200"
              >
                Close
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

export default InvoiceGenerator;
